import React, { useEffect } from "react";

import { useLocation } from "react-router-dom";

import ErrorBoundary from "Components/ErrorBoundary";
import PageLayout from "Components/PageLayout";
import { ACTIVITY_TYPES } from "Constants/constants";
import NavBar from "Containers/NavBar";
import SubNavBarContainer from "Containers/SubNavBarContainer";
import { useActivityListener } from "Hooks/useActivityListener";
import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import useUrls from "Hooks/useUrls";
import { selectCurrentUserIsInternal } from "Reducers/app/selectors";
import { environmentSelector, loadEnvironment } from "Reducers/environment";
import {
  loadDeploymentCurrent,
  loadDeploymentNext,
  deploymentStatusCodesSelector,
  nextDeployment as nextDeploymentSelector,
  currentDeploymentClassSelector,
  deploymentSizingApiEnabledSelector
} from "Reducers/environment/deployment";
import { selectFeatureObservabilitySandboxEnabled } from "Reducers/featureFlags/featureFlags.selectors";
import { loadObservabilityPipelineConfiguration } from "Reducers/observability/pipeline/thunks";
import { loadObservabilitySandboxAccess } from "Reducers/observability/sandbox/thunks";
import { organizationByDescriptionIdSelector } from "Reducers/organization";
import { loadProjectCapability } from "Reducers/project/capabilities";
import {
  getIsLoadingProjects,
  gitProjectSelector,
  loadOneProject
} from "Reducers/project/project";
import { useLoadSetupOptions } from "Reducers/setupOptions/hooks/useLoadSetupOptions";
import { useAppDispatch, useAppSelector } from "Store/hooks";

import ProjectError from "./containers/ProjectError";
import useObservabilityPipelineHttpClient from "./environment/observabilitySandbox/hooks/useObservabilityPipelineHttpClient";
import * as S from "./Page.styles";

type ProjectProps = {
  children: React.ReactNode;
  disableSubNavBar: boolean;
};

const Project = ({ children, disableSubNavBar }: ProjectProps) => {
  const dispatch = useAppDispatch();
  const { projectId, organizationId, environmentId } = useDecodedParams<{
    projectId: string;
    organizationId: string;
    environmentId?: string;
  }>();
  const { pathname } = useLocation();
  const [projectSettingUrl, environmentSettingUrl] = useUrls(
    [
      "organization.project.settings",
      "organization.project.environment.settings"
    ],
    { environmentId: environmentId ?? "" }
  );
  const disableFooter =
    pathname.startsWith(projectSettingUrl) ||
    pathname.startsWith(environmentSettingUrl);

  const organization = useAppSelector(state =>
    organizationByDescriptionIdSelector(state, {
      organizationDescriptionId: organizationId
    })
  );
  const project = useSelectorWithUrlParams(gitProjectSelector);
  const isInternalUser = useAppSelector(selectCurrentUserIsInternal);

  const environment = useSelectorWithUrlParams(environmentSelector);

  useEffect(() => {
    dispatch(loadOneProject({ projectId, organizationId }));
  }, [dispatch, organizationId, projectId]);

  useLoadSetupOptions(organization?.id);

  useEffect(() => {
    if (environmentId) {
      dispatch(loadEnvironment({ environmentId, projectId, organizationId }));
    }
  }, [dispatch, organizationId, projectId, environmentId]);

  const isLoadingProjects = useAppSelector(getIsLoadingProjects);

  useEffect(() => {
    project &&
      dispatch(
        loadProjectCapability({
          organizationId: organization?.id!,
          organizationName: organizationId,
          projectId
        })
      );
  }, [
    dispatch,
    organizationId,
    projectId,
    project,
    isLoadingProjects,
    organization?.id
  ]);

  const envId = (environmentId || project?.default_branch) as string;

  const { current: currentCode, next: nextCode } = useAppSelector(state =>
    deploymentStatusCodesSelector(state, {
      organizationId,
      projectId,
      environmentId: envId
    })
  );

  const currentDeployment = useAppSelector(state =>
    currentDeploymentClassSelector(state, {
      organizationId,
      projectId,
      environmentId: envId
    })
  );

  const nextDeployment = useAppSelector(state =>
    nextDeploymentSelector(state, {
      organizationId,
      projectId,
      environmentId: envId
    })
  );

  const isSizingApiEnabled = useSelectorWithUrlParams(
    deploymentSizingApiEnabledSelector
  );

  const getDeployments = async ({
    getCurrentDeployment,
    getNextDeployment
  }) => {
    if (getCurrentDeployment) {
      await dispatch(
        loadDeploymentCurrent({
          organizationId,
          projectId,
          environmentId: envId
        })
      );
    }

    if (getNextDeployment) {
      await dispatch(
        loadDeploymentNext({
          organizationId,
          projectId,
          environmentId: envId
        })
      );
    }
  };

  const obsSandboxEnabled = useAppSelector(
    selectFeatureObservabilitySandboxEnabled
  );
  const obsPipelineHttpClient = useObservabilityPipelineHttpClient();

  const machineName = environment?.machine_name;

  useEffect(() => {
    if (!machineName) {
      return;
    }

    dispatch(
      loadObservabilityPipelineConfiguration({
        projectId,
        machineName
      })
    );
  }, [dispatch, projectId, machineName]);

  useEffect(() => {
    if (!obsPipelineHttpClient || !obsSandboxEnabled) {
      return;
    }

    dispatch(loadObservabilitySandboxAccess({ client: obsPipelineHttpClient }));
  }, [dispatch, obsPipelineHttpClient, obsSandboxEnabled]);

  useEffect(() => {
    if (organizationId && projectId && envId) {
      getDeployments({
        getCurrentDeployment: !currentDeployment,
        getNextDeployment: !nextDeployment
      });
    }
    // Reason: adding "getDeployments" causes an infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    organizationId,
    projectId,
    envId,
    environment,
    currentDeployment,
    nextDeployment,
    currentCode,
    nextCode
  ]);

  useActivityListener({
    watchForActivityTypes: isSizingApiEnabled
      ? [
          ACTIVITY_TYPES.ENVIRONMENT.RESOURCES.UPDATE,
          ACTIVITY_TYPES.ENVIRONMENT.BRANCH,
          ACTIVITY_TYPES.ENVIRONMENT.SYNCHRONIZE,
          ACTIVITY_TYPES.ENVIRONMENT.RESTORE,
          ACTIVITY_TYPES.ENVIRONMENT.PUSH
        ]
      : "",
    onCompletion: () => {
      getDeployments({
        getCurrentDeployment: true,
        getNextDeployment: true
      });
    },
    context: "project"
  });

  return (
    <ProjectError>
      <NavBar
        projectId={projectId}
        organizationId={organizationId}
        environmentId={environmentId}
      />
      <PageLayout className="project" footer={!disableFooter}>
        {disableSubNavBar && !isInternalUser ? null : (
          <SubNavBarContainer
            currentPathName={pathname}
            organizationId={organizationId}
            environmentId={environmentId}
            projectId={projectId}
          />
        )}
        <ErrorBoundary>
          <S.Layout>{children}</S.Layout>
        </ErrorBoundary>
      </PageLayout>
    </ProjectError>
  );
};

export default Project;
